import React, { useState } from 'react';
import { Shield, KeyRound, Sparkles } from 'lucide-react';
import LoginForm from './LoginForm';
import SuperAdminLoginForm from './SuperAdminLoginForm';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const AuthScreen: React.FC = () => {
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [enteredPin, setEnteredPin] = useState('');
    const [pinVerifying, setPinVerifying] = useState(false);
    const [pinError, setPinError] = useState('');

    const handlePinChange = (val: string) => {
        // Only accept numbers, max 4 digits
        const numeric = val.replace(/\D/g, '').slice(0, 4);
        setEnteredPin(numeric);
    };

    const handleVerifyPin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (enteredPin.length < 4) {
            setPinError('Please enter a 4-digit pin');
            return;
        }

        setPinError('');
        setPinVerifying(true);

        try {
            // Check Firestore document `/adminAuth/lpLEadLlEcUNBmumGtjE`
            const docRef = doc(db, 'adminAuth', 'lpLEadLlEcUNBmumGtjE');
            const docSnap = await getDoc(docRef);
            
            let correctPin = '8043'; // Standard fallback pin
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data && data.pincode) {
                    correctPin = String(data.pincode);
                }
            }

            if (enteredPin === correctPin) {
                setIsSuperAdmin(true);
                setShowPinModal(false);
                setEnteredPin('');
            } else {
                setPinError('Access Denied: Invalid Security Pin.');
            }
        } catch (error) {
            // Local offline fallback to guarantee developer login
            if (enteredPin === '8043') {
                setIsSuperAdmin(true);
                setShowPinModal(false);
                setEnteredPin('');
            } else {
                setPinError('Access Denied: Invalid Offline PIN.');
            }
        } finally {
            setPinVerifying(false);
        }
    };

    return (
        <div className={`min-h-screen flex items-center justify-center p-4 transition-all duration-700 relative overflow-hidden ${
            isSuperAdmin 
                ? 'bg-slate-950 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(16,185,129,0.15),rgba(255,255,255,0))]' 
                : 'bg-gradient-to-br from-emerald-50 via-teal-50/30 to-slate-50'
        }`}>
            {/* Ambient Background Accents for Super Admin */}
            {isSuperAdmin && (
                <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none overflow-hidden">
                    <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-emerald-500/5 blur-[120px] animate-pulse"></div>
                    <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-teal-500/5 blur-[120px] animate-pulse"></div>
                </div>
            )}

            <div className="w-full max-w-md relative z-10">
                {/* Main Auth Form Container */}
                <div className={`transition-all duration-500 transform ${
                    isSuperAdmin 
                        ? 'translate-y-0 scale-100' 
                        : 'translate-y-0 scale-100'
                }`}>
                    {isSuperAdmin ? (
                        <SuperAdminLoginForm onBack={() => setIsSuperAdmin(false)} />
                    ) : (
                        <div className="bg-white/80 border border-slate-100 shadow-xl rounded-3xl p-8 backdrop-blur-xl relative overflow-hidden shadow-emerald-500/5">
                            <LoginForm />
                        </div>
                    )}
                </div>

                {/* Footer Section */}
                <div className={`text-center mt-10 text-xs transition-colors duration-500 ${
                    isSuperAdmin ? 'text-slate-500' : 'text-slate-400'
                }`}>
                    <p className="font-semibold tracking-wide">
                        Developed by <span className={isSuperAdmin ? 'text-emerald-400 font-extrabold' : 'text-slate-600 font-bold'}>DynamixZone</span>
                    </p>
                    <p className="mt-1">
                        All rights reserved as{' '}
                        <a
                            href="https://www.edulink.dynamixzone.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`font-semibold hover:underline transition-colors duration-300 ${
                                isSuperAdmin ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'
                            }`}
                        >
                            EDULINK (www.edulink.dynamixzone.com)
                        </a>
                    </p>
                </div>
            </div>

            {/* Bottom Right Floating Admin Gateway Button */}
            {!isSuperAdmin && (
                <button
                    type="button"
                    onClick={() => {
                        setPinError('');
                        setEnteredPin('');
                        setShowPinModal(true);
                    }}
                    className="fixed bottom-6 right-6 p-3 bg-white/90 hover:bg-emerald-50 text-slate-500 hover:text-emerald-600 rounded-2xl shadow-lg border border-slate-200/80 hover:border-emerald-200 backdrop-blur-md transition-all duration-300 hover:scale-110 hover:rotate-12 group z-40 focus:outline-none"
                    title="Super Admin Gateway"
                >
                    <Shield className="h-5 w-5 stroke-[2px] transition-transform duration-300 group-hover:scale-105" />
                    <span className="absolute right-full mr-2.5 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-[10px] uppercase tracking-wider font-extrabold px-2.5 py-1 rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-all duration-300 shadow-md">
                        Gateway
                    </span>
                </button>
            )}

            {/* Premium 4-Digit Security PIN Modal */}
            {showPinModal && (
                <Modal
                    isOpen={true}
                    onClose={() => !pinVerifying && setShowPinModal(false)}
                    title="Super Admin Authentication"
                    size="sm"
                    footer={
                        <>
                            <Button 
                                variant="secondary" 
                                type="button" 
                                onClick={() => setShowPinModal(false)} 
                                disabled={pinVerifying}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="submit" 
                                form="pin-auth-form" 
                                disabled={pinVerifying || enteredPin.length < 4}
                                loading={pinVerifying}
                            >
                                Authenticate
                            </Button>
                        </>
                    }
                >
                    <form id="pin-auth-form" onSubmit={handleVerifyPin} className="space-y-6 py-2">
                        <div className="text-center">
                            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-emerald-600 animate-pulse">
                                <KeyRound className="h-6 w-6" />
                            </div>
                            <h4 className="text-sm font-bold text-slate-800">Security Gate Required</h4>
                            <p className="text-xs text-slate-500 mt-1">Please enter the 4-digit authorization code to unlock the Super Admin Panel.</p>
                        </div>

                        {pinError && (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-3.5 py-2.5 rounded-xl text-xs font-semibold text-center">
                                {pinError}
                            </div>
                        )}

                        <div className="relative max-w-[200px] mx-auto">
                            {/* Hidden Input field for focusing */}
                            <input
                                type="text"
                                pattern="\d*"
                                inputMode="numeric"
                                value={enteredPin}
                                onChange={(e) => handlePinChange(e.target.value)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                                maxLength={4}
                                autoFocus
                                required
                            />

                            {/* Symmetrical visual box matrix */}
                            <div className="flex justify-between items-center space-x-3.5 relative z-10 pointer-events-none">
                                {[0, 1, 2, 3].map((index) => {
                                    const char = enteredPin[index];
                                    const isActive = enteredPin.length === index;
                                    return (
                                        <div
                                            key={index}
                                            className={`w-11 h-12 border-2 rounded-xl flex items-center justify-center transition-all duration-300 font-extrabold text-lg ${
                                                char 
                                                    ? 'border-emerald-500 bg-emerald-50/20 text-emerald-600 shadow-sm shadow-emerald-500/5' 
                                                    : isActive 
                                                        ? 'border-emerald-400 ring-2 ring-emerald-500/10 scale-105 bg-white text-slate-700' 
                                                        : 'border-slate-200 bg-slate-50 text-slate-400'
                                            }`}
                                        >
                                            {char ? (
                                                <span className="block w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
                                            ) : (
                                                ''
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="text-center">
                            <span className="inline-flex items-center space-x-1 text-[10px] text-emerald-600/70 font-semibold tracking-wide uppercase">
                                <Sparkles className="h-3 w-3" />
                                <span>Zero-Trust Security Active</span>
                            </span>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
};

export default AuthScreen;
